import MultiElementSelector from './MultiElementSelector';
import { Dispatch, SetStateAction } from 'react';
import { PlutoPerson } from '@/generated/model';
import { countPeopleExtended, getPeople } from '@/generated/client/person/person';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { TargetNameAndDescription, targetNameAndDescriptionStyle } from '../TargetNameAndDescription';
import { useTranslation } from 'react-i18next';
import { norm } from '@/components/db/initDb';

export default function MultiDriverSelector({
  selectedDrivers,
  setSelectedDrivers,
  searchText,
  setSearchText,
  maxElementNumber,
  showMoreOptions,
}: {
  selectedDrivers: PlutoPerson[];
  setSelectedDrivers: Dispatch<SetStateAction<PlutoPerson[]>>;
  searchText: string | undefined;
  setSearchText: Dispatch<SetStateAction<string | undefined>>;
  maxElementNumber: number;
  showMoreOptions: boolean;
}) {
  async function fetchData(
    page: number,
    pageSize: number,
    pagesToLoad: number,
    filter: string | undefined,
    order: string
  ) {
    const drivers = await getPeople({
      offset: page * pageSize,
      limit: pageSize * pagesToLoad,
      filter: filter,
      order: order,
      showDeactivated: showMoreOptions,
    });
    return drivers.data;
  }
  const { t } = useTranslation();

  async function getRowCount(filter: string | undefined, ids: number[]) {
    const localFilter = filter ? filter : '';
    const count = await countPeopleExtended({ filter: localFilter, ids: ids, showDeactivated: showMoreOptions });
    return count.data;
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.1,
      renderCell: (params: GridRenderCellParams) => {
        const driver = params.row;
        const checked = selectedDrivers
          ? selectedDrivers.find((selectedDriver) => selectedDriver.id === driver.id) !== undefined
          : false;
        return (
          <div className="target-cell" data-qa-element-id="driver_icon">
            <TargetNameAndDescription
              targetName={driver.name}
              targetDescription={driver.customerName}
              styleClassName={targetNameAndDescriptionStyle(checked, true, !driver.isActive)}
            />
          </div>
        );
      },
    },
  ];

  const translation = {
    header: t('filter.driverSelector'),
    placeHolder: t('filter.driverSearchPlaceholder'),
    noRows: t('filter.noRows'),
    tooManySelected: t('error.api.too_many_driver_selected', { limit: maxElementNumber }),
  };

  function searchFilter(person: PlutoPerson, localSearchText: string) {
    return (
      (!!person.name && norm(person.name).indexOf(norm(localSearchText)) !== -1) ||
      (!!person.customerName && norm(person.customerName).indexOf(norm(localSearchText)) !== -1)
    );
  }

  return (
    <MultiElementSelector<PlutoPerson>
      selectedElements={selectedDrivers}
      setSelectedElements={setSelectedDrivers}
      searchText={searchText}
      setSearchText={setSearchText}
      maxElementNumber={maxElementNumber}
      fetchData={fetchData}
      getRowCount={getRowCount}
      columns={columns}
      translation={translation}
      showMoreOptions={showMoreOptions}
      searchFilter={searchFilter}
      searchHistoryKey="driver"
    />
  );
}
